<!--
// app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchEvents.vue
-->
<script setup lang="ts">
import type { Match } from "#shared/match/domain/models/Match";
import type { Event } from "#shared/match/domain/models/Event";
import type { Player } from "#shared/player/domain/Player";
import matchService, {
  $MATCH_DICE_PARAMS,
  $MATCH_TIMELINE_EVENT_PARAMS,
} from "#shared/match/domain/services/matchService";
import MatchEventsBreakEvent from "~/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchEventsBreakEvent.vue";
import MatchEventsLogosVs from "~/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchEventsLogosVs.vue";
import MatchEventsEvent from "~/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchEventsEvent.vue";
import MatchEventsEventMobile from "~/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchEventsEventMobile.vue";
import matchUiService from "#shared/match/infrastructure/ui-services/matchUiService";

type Props = {
  match: Match;
};

const props = defineProps<Props>();

const { transformStatus } = matchUiService();
const { isAwayTeamEvent } = matchService();
const data = reactive({
  hoveredIndexHomeTeam: -1,
  hoveredIndexAwayTeam: -1,
});

const {
  participants: { homeTeam, awayTeam },
} = props.match;
const allPlayers: Player[] = !!homeTeam && !!awayTeam ? [...homeTeam.players, ...awayTeam.players] : [];

const timeLineEvents = computed((): Event[] => {
  const events = props.match.events
    ? props.match.events.sort((a, b) => {
        return a.matchSecond > b.matchSecond ? 1 : a.matchSecond < b.matchSecond ? -1 : 0;
      })
    : [];

  return events.filter((event: Event) => {
    if (event.half > 2) return false;
    const isHomeTeamEvent =
      event.parameterAppString &&
      $MATCH_TIMELINE_EVENT_PARAMS.some(code => event.parameterAppString.includes(code));
    const isDiceEvent =
      event.parameterAppString && $MATCH_DICE_PARAMS.some(code => event.parameterAppString.includes(code));
    return isHomeTeamEvent || isDiceEvent;
  });
});

const firstHalfEvents = computed((): Event[] => {
  return timeLineEvents.value.filter(e => e.half === 1);
});

const secondHalfEvents = computed((): Event[] => {
  return timeLineEvents.value.filter(e => e.half === 2);
});

const isSecondHalf = computed((): boolean => {
  const events = props.match.events;
  return !!events?.length && !!events.find(e => e.half === 2);
});

const dummyEvents = computed(() => {
  if (transformStatus(props.match.status) === "ended") return 0;
  const minEvents = 8;
  const amount = minEvents - secondHalfEvents.value.length - firstHalfEvents.value.length;
  return amount < 0 ? 0 : amount;
});
</script>

<template>
  <div class="events-wrapper hidden md:flex">
    <match-events-logos-vs :home-team="homeTeam" :away-team="awayTeam"></match-events-logos-vs>

    <div class="events-timeline">
      <div class="events-line"></div>
      <div v-for="event in firstHalfEvents" class="h-full events-event events-event--1t">
        <match-events-event
          :players="allPlayers"
          :event="event"
          :is-away-team="isAwayTeamEvent(event, homeTeam?.id, awayTeam?.id)"
        />
      </div>

      <match-events-break-event v-if="isSecondHalf"></match-events-break-event>

      <div v-for="event in secondHalfEvents" class="h-full events-event events-event--2t">
        <match-events-event
          :players="allPlayers"
          :event="event"
          :is-away-team="isAwayTeamEvent(event, homeTeam?.id, awayTeam?.id)"
        />
      </div>

      <div v-for="dummy in dummyEvents"></div>
    </div>
  </div>

  <div class="events-wrapper-mob flex md:hidden">
    <match-events-logos-vs :home-team="homeTeam" :away-team="awayTeam"></match-events-logos-vs>

    <div class="events-timeline-mob">
      <div class="events-line-mob"></div>
      <div v-for="event in firstHalfEvents" class="w-full events-event events-event--1t">
        <match-events-event-mobile
          :players="allPlayers"
          :event="event"
          :is-away-team="isAwayTeamEvent(event, homeTeam?.id, awayTeam?.id)"
        />
      </div>

      <match-events-break-event v-if="isSecondHalf"></match-events-break-event>

      <div v-for="event in secondHalfEvents" class="w-full events-event events-event--2t">
        <match-events-event-mobile
          :players="allPlayers"
          :event="event"
          :is-away-team="isAwayTeamEvent(event, homeTeam?.id, awayTeam?.id)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.events-wrapper {
  @apply items-center px-4 gap-4 mt-20 mx-8;
}

.events-timeline {
  @apply flex justify-around items-center pl-2 relative w-full h-[120px];
}

.events-line {
  @apply h-[1px] bg-gray-700 absolute left-0 top-[50%] w-full;
  @apply before:absolute before:h-[8px] before:w-[1px] before:bg-gray-700 before:left-0 before:-top-[4px];
  @apply after:absolute after:h-[8px] after:w-[1px] after:bg-gray-700 after:right-0 after:-top-[4px];
}

.events-wrapper-mob {
  @apply flex-col items-center px-1 gap-4 mt-12 mx-2;
}

.events-timeline-mob {
  @apply flex flex-col justify-around items-center relative h-full w-full min-w-[120px];
}

.events-line-mob {
  @apply w-[1px] bg-gray-700 absolute top-0 left-[50%] h-full;
  @apply before:absolute before:w-[8px] before:h-[1px] before:bg-gray-700 before:top-0 before:-left-[4px];
  @apply after:absolute after:w-[8px] after:h-[1px] after:bg-gray-700 after:bottom-0 after:-right-[4px];
}
</style>
